import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import BtnPrimary from "../../../common/element/Buttons/btnPrimary";
import Card from "../../../common/element/Card";
import Heading from "../../../common/element/Heading";
import Circle from "../../../common/element/Iconcircle";
import { Link } from "react-router-dom";
import ThreeDotLoader from "../../../common/element/ButtonLoader/ThreeDotLoader";
import { useNavigate, useLocation } from "react-router-dom";
import { resetPassword, clearResetPassword } from "../../../redux/auth/authSlice";
import { 
  selectResetPasswordData, 
  selectResetPasswordLoading, 
  selectResetPasswordError 
} from "../../../redux/auth/authSelector";
import SweetAlertService from "../../../common/SweetAlert/SweetAlert";

function ResetPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [repeatPasswordError, setRepeatPasswordError] = useState("");
  const [token, setToken] = useState("");
  const [hiddenpassword, sethiddenpassword] = useState(true);
  const [hiddenOldpassword, setHiddenOldpassword] = useState(true);

  // Redux selectors
  const resetPasswordData = useSelector(selectResetPasswordData);
  const isLoading = useSelector(selectResetPasswordLoading);
  const resetPasswordError = useSelector(selectResetPasswordError);

  // Extract token from URL on mount
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const tokenFromUrl = urlSearchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    }
  }, [location.search]);

  // Handle success/error from Redux
  useEffect(() => {
    if (resetPasswordData) {
      // Show success alert
      SweetAlertService.showSuccess({
        title: 'Success!',
        subtitle: 'Password Reset Successfully',
        message: 'Your password has been reset successfully. You can now login with your new password.',
        confirmButtonText: 'Go to Login',
        allowClose: false,
        onConfirm: () => {
          dispatch(clearResetPassword());
          navigate("/login");
        }
      });
    }

    if (resetPasswordError) {
      // Show error alert
      SweetAlertService.showError({
        title: 'Error!',
        message: resetPasswordError || 'Failed to reset password. Please try again.',
        confirmButtonText: 'OK',
        onConfirm: () => {
          dispatch(clearResetPassword());
        }
      });
    }
  }, [resetPasswordData, resetPasswordError, dispatch, navigate]);

  const togglePassword = (check) => {
    if (check === "new") {
      sethiddenpassword(!hiddenpassword);
    } else {
      setHiddenOldpassword(!hiddenOldpassword);
    }
  };

  const handleNewPassword = (event) => {
    setNewPassword(event.target.value);
    setPasswordError(""); // Clear error on input
  };

  const handleRepeatPassword = (event) => {
    setRepeatPassword(event.target.value);
    setRepeatPasswordError(""); // Clear error on input
  };

  const handleErrorMessage = () => {
    const isStrong = newPassword.match(
      "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#%&])(?=.{8,})"
    );
    setPasswordError(
      isStrong
        ? ""
        : "Your password must be at least 8 characters long, contain at least one special character, and have a mixture of lowercase, uppercase, and numbers."
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Clear previous errors
    setPasswordError("");
    setRepeatPasswordError("");

    // Check if token exists
    if (!token) {
      setPasswordError("Invalid or missing reset token. Please request a new password reset link.");
      return;
    }

    // Validate Password
    const isStrong = newPassword.match(
      "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#%&])(?=.{8,})"
    );

    if (!isStrong) {
      setPasswordError(
        "Your password must be at least 8 characters long, contain at least one special character, and have a mixture of lowercase, uppercase, and numbers."
      );
      return;
    }

    if (!repeatPassword) {
      setRepeatPasswordError("To continue, please enter your confirmed password");
      return;
    }

    if (newPassword !== repeatPassword) {
      setRepeatPasswordError("Passwords do not match");
      return;
    }

    // Prepare payload (only password, token goes in header)
    const payload = {
      password: repeatPassword, // Sending repeatPassword as password
    };

    // Dispatch reset password action with token for header
    dispatch(resetPassword({ payload, token }));
  };

  return (
    <div
      style={{ backgroundColor: `rgb(255, 244, 229)` }}
      className="flex justify-center min-h-screen"
    >
      <div className="min-h-screen justify-center flex py-12 px-6">
        <div className="flex-col items-center justify-center flex">
          <Card className="w-500 rounded-xl block-shadow w-100 mt-2 px-10 py-8">
            <div className="w-full">
              <Circle className="bg-[#FFF4E5] border-blue-50 mb-6">
                <img src="/assets/icons-svg/key.svg" alt="key" />
              </Circle>
                          <h4 className="font-lato font-semibold text-[30px] leading-[38px] text-[#101828] text-wrap lg:text-nowrap text-center">Set new password</h4>

            
              <p className="text-sm text-gray-600 text-center mb-6">
                Your new password must be different to previously <br />
                used passwords.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="mb-5">
                  <label
                    className="block text-gray-700 mb-2 text-xs font-medium text-start"
                    htmlFor="New-password" 
                  >
                    New password
                  </label>
                  <div className="relative flex">
                    <input
                      className="input-shadow h-10 text-sm border-gray-300 rounded-lg appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="New-password"
                      type={hiddenpassword ? "password" : "text"}
                      value={newPassword}
                      placeholder="••••••••"
                      onChange={handleNewPassword}
                      onBlur={handleErrorMessage}
                      disabled={isLoading}
                    />
                    <span
                      className="absolute right-4 top-3 cursor-pointer"
                      onClick={() => togglePassword("new")}
                    >
                      {hiddenpassword ? (
                        <svg
                          stroke="currentColor"
                          fill="none"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          height="21"
                          width="20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            stroke="#667085"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          ></path>
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="21"
                          viewBox="0 0 20 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.017 11.094c-.114-.18-.17-.27-.202-.408a.977.977 0 0 1 0-.372c.032-.139.088-.229.202-.408.938-1.485 3.73-5.24 7.983-5.24 4.255 0 7.046 3.755 7.984 5.24.113.18.17.27.202.408a.976.976 0 0 1 0 .372c-.032.139-.089.229-.202.408-.938 1.485-3.73 5.24-7.984 5.24s-7.045-3.755-7.983-5.24z"
                            stroke="#667085"
                            strokeWidth="1.667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 13a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"
                            stroke="#667085"
                            strokeWidth="1.667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                  </div>
                  {passwordError && (
                    <p className="text-xs text-red-600 mt-2">{passwordError}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label
                    className="block text-gray-700 mb-2 text-xs font-medium text-start"
                    htmlFor="repeat-password"
                  >
                    Repeat new password
                  </label>
                  <div className="relative flex">
                    <input
                      className="input-shadow h-10 text-sm border-gray-300 rounded-lg appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="repeat-password"
                      type={hiddenOldpassword ? "password" : "text"}
                      value={repeatPassword}
                      placeholder="••••••••"
                      onChange={handleRepeatPassword}
                      disabled={isLoading}
                    />
                    <span
                      className="absolute right-4 top-3 cursor-pointer"
                      onClick={() => togglePassword("repeat")}
                    >
                      {hiddenOldpassword ? (
                        <svg
                          stroke="currentColor"
                          fill="none"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          height="21"
                          width="20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            stroke="#667085"
                            strokeLinejoin="round"
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          ></path>
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="21"
                          viewBox="0 0 20 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.017 11.094c-.114-.18-.17-.27-.202-.408a.977.977 0 0 1 0-.372c.032-.139.088-.229.202-.408.938-1.485 3.73-5.24 7.983-5.24 4.255 0 7.046 3.755 7.984 5.24.113.18.17.27.202.408a.976.976 0 0 1 0 .372c-.032.139-.089.229-.202.408-.938 1.485-3.73 5.24-7.984 5.24s-7.045-3.755-7.983-5.24z"
                            stroke="#667085"
                            strokeWidth="1.667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 13a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"
                            stroke="#667085"
                            strokeWidth="1.667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                  </div>
                  {repeatPasswordError && (
                    <p className="text-xs text-red-600 mt-2 text-start">{repeatPasswordError}</p>
                  )}
                </div>

                <div className="flex items-center justify-between w-100">
                  <BtnPrimary disabled={isLoading} type="submit">
                    {isLoading ? <ThreeDotLoader /> : "Reset password"}
                  </BtnPrimary>
                </div>
              </form>
            </div>
          </Card>
          <div className="mt-8 px-10 w-full text-center">
            <Link
              to="/login"
              className="text-xs text-gray-600 font-semibold hover:text-gray-900 focus:text-gray-600 flex justify-center items-center"
            >
              <img src="/assets/icons-svg/arrow-left.svg" className="w-5 mr-2" alt="back" />
              Back to log in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;